<ui-modal modalTitle="Advanced Search" size="xxl" [footer]="false" [fullHeight]="true">
	<!-- Search sits beside the close button, with the age of the results on screen ahead of it: they survive the modal
		closing, so how old they are has to be on show. -->
	<div class="d-flex align-items-center justify-content-end" ui-modal-header>
		<div class="text-secondary text-small nowrap mr-3" *ngIf="resultsAge">
			<i class="wq wq-clock mr-1"></i>Fetched {{resultsAge}}
		</div>

		<div class="input-group mr-3 search-box" #searchBox>
			<div class="input-group-prepend">
				<span class="input-group-text"><i class="wq wq-search"></i></span>
			</div>
			<!-- type="search" gives the same native clear button every other search box in the app uses. -->
			<input *ngIf="!!modal?.isOpen" type="search" class="form-control" [placeholder]="searchPlaceholder" [(ngModel)]="searchQuery" (input)="onSearchInput()" (keyup.enter)="search()" (focus)="showRecentSearch()" autocomplete="chrome-off" spellcheck="false">
			<div class="input-group-append">
				<button type="button" class="btn btn-primary px-4" [disabled]="!canSearch" (click)="search()">Search</button>
			</div>

			<div class="suggestions-overlay shadow bg-white border border-lightgrey rounded mt-1" *ngIf="showsRecentSearches">
				<div class="text-uppercase text-tiny text-secondary px-3 pt-3 pb-1">Recent searches</div>
				<div *ngFor="let search of recentSearches" class="px-3 py-2 selectable hoverable d-flex align-items-center" (click)="recentSearch(search)">
					<i class="wq wq-clock text-secondary mr-2"></i>
					<span class="text-dark flex-weight-1 text-truncate">{{search}}</span>
				</div>
			</div>
		</div>
	</div>

	<div class="d-flex flex-row mt-n4 h-100 global-search-body">
		<!-- The product panel takes over the whole body: the filters and tabs belong to the result list, not to it. -->
		<div class="d-flex flex-column border-right" *ngIf="!showsProductDetail">
			<app-filter
				(filterChanged)="applyFilters($event)"
				(scopeChanged)="scopeChanged($event)"
				[sections]="filterSections"
				[segments]="showsCategoryTree ? segments : null"
				[totalCategories]="totalCategories"
				[scope]="scope"
				[selected]="selectedFilters"
				[loading]="searchResults === null"
				[treeLoading]="browseLoading"></app-filter>
		</div>

		<div class="d-flex flex-column flex-weight-1">
			<div class="d-flex flex-row border-bottom" *ngIf="!showsProductDetail">
				<div class="d-flex flex-weight-1">
					<div *ngFor="let t of tabList" class="px-3 py-4 flex-weight-1 hoverable d-flex align-items-center border-bottom" [ngClass]="{ 'border-4 border-primary text-bold': activeTab === t.id, 'border-transparent': activeTab !== t.id, 'selectable': !t.disabled }" (click)="selectTab(t)">
						<span class="text-uppercase text-small text-bold mr-2" [ngClass]="{ 'text-secondary': activeTab !== t.id }"><i class="mr-2" [ngClass]="t.icon"></i>{{t.title}}</span>
						<span class="badge badge-pill" [ngClass]="activeTab === t.id ? 'badge-primary' : 'badge-lightergrey text-secondary'">{{tabCount(t.id)}}</span>
					</div>
				</div>
			</div>

			<!-- Nothing typed yet: offer the catalogue to browse rather than an empty table. -->
			<ng-container *ngIf="browsing; else resultsView">
				<div class="flex-weight-1 d-flex flex-column p-3 browse-view">
					<div class="text-uppercase text-tiny text-secondary mb-2">Browse</div>

					<div class="d-flex flex-row flex-weight-1">
						<div class="d-flex flex-column flex-weight-1">
							<!-- alwaysShowHeader so the card keeps its title while the tab's counts are being fetched. -->
							<ui-card widget header="Browse categories" [headerDivider]="true" [wait]="!browseLoading && browseCategories" [alwaysShowHeader]="true" [padding]="false">
								<table class="table table-hover m-0">
									<tbody>
										<tr *ngFor="let category of browseCategories" class="selectable" (click)="browseCategory(category)">
											<td class="text-bold text-dark">{{category.category}}</td>
											<td class="text-secondary text-right nowrap">{{category.segment}}</td>
											<td class="text-secondary text-right nowrap">{{category.product_count | decimal:0}} {{browseCountLabel}}</td>
											<td class="shrink text-secondary"><i class="wq wq-angle-right"></i></td>
										</tr>
										<tr *ngIf="!browseCategories?.length">
											<td class="text-secondary">No categories found.</td>
										</tr>
									</tbody>
								</table>
							</ui-card>
						</div>
					</div>
				</div>
			</ng-container>

			<ng-template #resultsView>
				<ui-card widget class="flex-weight-1 results-card" [footer]="!showsProductDetail" [wait]="searchResults" [customHeader]="!showsProductDetail" [customHeaderDivider]="false" [headerDivider]="false">
					<!-- Result count, scope chips and the cap notice live in the card header, so they stay put while the table scrolls. -->
					<div ui-card-custom-header>
						<div class="d-flex align-items-center flex-wrap">
							<span class="text-uppercase text-tiny text-secondary mr-2">{{resultsLabel}} &middot; {{totalGroups}}<span *ngIf="approximate">+</span><span *ngIf="resultsTerm || hasFilters"> {{totalGroups === 1 ? 'result' : 'results'}}</span></span>

							<span *ngIf="scope.segment" class="badge badge-lightergrey text-dark text-small font-weight-normal mr-2 px-2 py-1">
								{{scope.segment}} <i class="wq wq-close selectable ml-1" (click)="removeScope('segment')"></i>
							</span>
							<span *ngIf="scope.category" class="badge badge-lightergrey text-dark text-small font-weight-normal mr-2 px-2 py-1">
								{{scope.category}} <i class="wq wq-close selectable ml-1" (click)="removeScope('category')"></i>
							</span>
							<span *ngIf="scope.subcategories?.length" class="badge badge-lightergrey text-dark text-small font-weight-normal mr-2 px-2 py-1">
								{{scope.subcategories.length}} {{scope.subcategories.length === 1 ? 'subcategory' : 'subcategories'}} <i class="wq wq-close selectable ml-1" (click)="removeScope('subcategories')"></i>
							</span>

							<!-- The active tab's selected facets, so what's narrowing the results is visible without opening the panel. -->
							<span *ngFor="let chip of filterChips" class="badge badge-lightergrey text-dark text-small font-weight-normal mr-2 px-2 py-1 d-inline-flex align-items-center">
								<span *ngIf="chip.color" class="label-dot rounded-circle mr-1" [style.background]="chip.color"></span>
								{{chip.label}} <i class="wq wq-close selectable ml-1" (click)="removeFilter(chip)"></i>
							</span>
						</div>

						<div *ngIf="capped" class="alert alert-warning py-2 mt-2 mb-0 text-small">
							Showing the first {{searchResults?.length}} of {{totalGroups}}<span *ngIf="approximate">+</span> matches &mdash; refine your search to narrow it down.
						</div>
					</div>

					<ng-container *ngIf="searchResults">
						<!-- A clicked product takes over the results area; the panel's own back button returns to the list. -->
						<div *ngIf="showsProductDetail" class="results-body detail-scroll pt-3 pr-1">
							<app-product-detail
								[detail]="productDetail"
								[loading]="productDetailLoading"
								[canAdd]="canAdd()"
								[addedProducts]="addedProducts"
								[loadingSections]="productSectionsLoading"
								[orgAlias]="orgAlias"
								(sectionOpened)="loadProductSection($event)"
								(closed)="closeProduct()"
								(add)="addProduct($event)"
								(openProduct)="selectProduct($event)"
								(editProduct)="editProduct($event)">
							</app-product-detail>
						</div>

						<div *ngIf="!showsProductDetail" class="d-flex flex-row align-items-stretch results-body">
							<div class="flex-weight-1 results-scroll">
								<div *ngIf="!tabImplemented" class="text-secondary">Search for {{activeTab}} is not available yet.</div>

								<!-- Stock -->
								<table *ngIf="activeTab === 'stock'" class="table table-hover">
									<thead>
										<tr>
											<th class="sticky text-dark" colspan="2">Product</th>
											<th class="sticky text-dark">Type</th>
											<th class="sticky text-dark">Held in</th>
											<th class="sticky text-dark">Quantities</th>
											<th class="sticky text-dark shrink"></th>
										</tr>
									</thead>
									<tbody>
										<tr *ngFor="let item of searchResults | pagination:pagination" class="selectable detail-row"
											[ngClass]="{ 'is-selected': selectedRecord?.item_key === item.item_key }"
											(click)="selectRecord(item)">
											<td class="shrink pr-0 align-middle">
												<div *ngIf="item.image_url" class="product-image mr-3" [style.backgroundImage]="'url(\'' + item.image_url + '\')' | safeStyle"></div>
											</td>
											<td class="pl-0 align-middle">
												<div class="text-bold" [innerHTML]="(item.short_description || item.model || item.warehouse_sku) | highlight: resultsTerm | safeHtml"></div>
												<div class="text-secondary text-tiny">
													<span *ngIf="item.manufacturer_name" [innerHTML]="item.manufacturer_name | highlight: resultsTerm | safeHtml"></span>
													<span *ngIf="item.manufacturer_name && (item.manufacturer_sku || item.warehouse_sku)"> / </span>
													<span [innerHTML]="(item.manufacturer_sku || item.warehouse_sku) | highlight: resultsTerm | safeHtml"></span>
													<span *ngIf="item.warehouse_sku"> &middot; <span [innerHTML]="item.warehouse_sku | highlight: resultsTerm | safeHtml"></span></span>
												</div>
											</td>
											<td class="align-middle">
												<span class="badge text-tiny text-uppercase" [ngClass]="item.is_serialised ? 'badge-purple' : 'badge-lightergrey text-secondary'">{{item.is_serialised ? 'Serialised' : 'Non-serialised'}}</span>
											</td>
											<td class="align-middle text-small">
												<div>Warehouses <span class="text-bold">{{item.warehouses | decimal:0}}</span> &middot; Sites <span class="text-bold">{{item.sites | decimal:0}}</span></div>
												<div>Vans <span class="text-bold">{{item.vans | decimal:0}}</span> &middot; Locations <span class="text-bold">{{item.locations | decimal:0}}</span></div>
											</td>
											<td class="align-middle text-small">
												<div>On-hand <span class="text-bold">{{item.quantity | decimal:0:4:true}}</span> &middot; Free <span class="text-bold text-success">{{item.quantity_available | decimal:0:4:true}}</span></div>
												<div>Allocated <span class="text-bold text-info">{{item.quantity_allocated | decimal:0:4:true}}</span> &middot; Reserved <span class="text-bold text-warning">{{item.quantity_reserved | decimal:0:4:true}}</span></div>
											</td>
											<td class="shrink align-middle text-secondary"><i class="wq wq-angle-right"></i></td>
										</tr>
										<tr *ngIf="!pagination.count">
											<td colspan="6">
												<div class="text-center py-5">
													<div class="h3 mb-3">No stock found<span *ngIf="resultsTerm"> for &ldquo;{{resultsTerm}}&rdquo;</span><span *ngIf="hasFilters"> with these filters</span></div>
													<button *ngIf="hasFilters" type="button" class="btn btn-outline-secondary" (click)="clearFilters()">Clear filters</button>
												</div>
											</td>
										</tr>
									</tbody>
								</table>

								<!-- Customers -->
								<table *ngIf="activeTab === 'customers'" class="table table-hover">
									<thead>
										<tr>
											<th class="sticky text-dark">Customer</th>
											<th class="sticky text-dark">Stats</th>
											<th class="sticky text-dark text-right">Projects</th>
											<th class="sticky text-dark text-right">Quoted</th>
											<th class="sticky text-dark shrink"></th>
										</tr>
									</thead>
									<tbody>
										<tr *ngFor="let customer of searchResults | pagination:pagination" class="selectable detail-row"
											[ngClass]="{ 'is-selected': selectedRecord?.id === customer.id }"
											(click)="selectRecord(customer)">
											<td>
												<div class="d-flex align-items-center">
													<span class="text-bold" [innerHTML]="customer.name | highlight: resultsTerm | safeHtml"></span>
													<span *ngIf="customer.account_no" class="text-secondary text-small ml-2" [innerHTML]="customer.account_no | highlight: resultsTerm | safeHtml"></span>
												</div>
												<div class="text-secondary text-small">
													<span *ngIf="customer.contact_first_name || customer.contact_surname">{{customer.contact_first_name}} {{customer.contact_surname}}</span>
													<span *ngIf="(customer.contact_first_name || customer.contact_surname) && (customer.contact_email || customer.email_address)"> &middot; </span>
													<span>{{customer.contact_email || customer.email_address}}</span>
												</div>
											</td>
											<td class="text-small">
												<span *ngIf="customerUsage(customer)">{{customerUsage(customer)}}</span>
												<span *ngIf="!customerUsage(customer)" class="text-secondary">no records</span>
												<span *ngIf="customer.has_open_quotes" class="badge badge-success text-tiny ml-2">Open</span>
											</td>
											<td class="text-right text-bold">{{customer.projects | decimal:0}}</td>
											<td class="text-right">{{customer.quoted_total | currencyFlexible:currencyId}}</td>
											<td class="shrink text-secondary"><i class="wq wq-angle-right"></i></td>
										</tr>
								<tr *ngIf="!pagination.count">
											<td colspan="5">
												<div class="text-center py-5">
													<div class="h3 mb-3">No customers found<span *ngIf="resultsTerm"> for &ldquo;{{resultsTerm}}&rdquo;</span><span *ngIf="hasFilters"> with these filters</span></div>
													<button *ngIf="hasFilters" type="button" class="btn btn-outline-secondary" (click)="clearFilters()">Clear filters</button>
												</div>
											</td>
										</tr>
									</tbody>
								</table>

								<!-- Users -->
								<table *ngIf="activeTab === 'users'" class="table table-hover">
									<thead>
										<tr>
											<th class="sticky text-dark">User</th>
											<th class="sticky text-dark">Stats</th>
											<th class="sticky text-dark text-right">Records</th>
										</tr>
									</thead>
									<tbody>
									<tr *ngFor="let user of searchResults | pagination:pagination" class="selectable detail-row"
											[ngClass]="{ 'is-selected': selectedRecord?.id === user.id }"
											(click)="selectRecord(user)">
											<td class="d-flex align-items-center">
												<ui-user-image class="mr-3" [email]="user.email_addr" size="2.5rem"></ui-user-image>
												<div>
													<div class="text-bold" [innerHTML]="user.name | highlight: resultsTerm | safeHtml"></div>
													<div class="text-secondary text-tiny">{{user.role_name || 'No role'}}</div>
												</div>
											</td>
											<td class="text-small">
												<span *ngIf="user.total_documents">{{documentSummary(user)}}</span>
												<span *ngIf="!user.total_documents" class="text-secondary">no records</span>
											</td>
											<td class="text-right text-bold">{{user.total_documents | decimal:0}}</td>
										</tr>
									<tr *ngIf="!pagination.count">
											<td colspan="3">
												<div class="text-center py-5">
													<div class="h3 mb-3">No users found<span *ngIf="resultsTerm"> for &ldquo;{{resultsTerm}}&rdquo;</span><span *ngIf="hasFilters"> with these filters</span></div>
													<button *ngIf="hasFilters" type="button" class="btn btn-outline-secondary" (click)="clearFilters()">Clear filters</button>
												</div>
											</td>
										</tr>
									</tbody>
								</table>

								<!-- Journal -->
								<div *ngIf="activeTab === 'journal'">
									<div *ngFor="let entry of searchResults | pagination:pagination" class="selectable hoverable detail-row border-bottom px-3 py-3"
										[ngClass]="{ 'is-selected': selectedRecord?.key === entry.key }"
										(click)="selectRecord(entry)">
										<div class="d-flex align-items-center mb-2">
											<span class="badge badge-lightergrey text-dark text-tiny text-uppercase nowrap mr-2"><i class="wq mr-1" [ngClass]="journalKindIcon(entry)"></i>{{journalKindLabel(entry)}}</span>
											<!-- The record type, badged in its group's colour: Sales & CRM, Customers & Projects, Work Hub, Procurement & Inventory. -->
											<span class="flex-weight-1 text-truncate">
												<span class="badge text-tiny text-uppercase nowrap" [ngClass]="journalSourceBadge(entry)"><i *ngIf="journalSourceIcon(entry)" class="wq mr-1" [ngClass]="journalSourceIcon(entry)"></i>{{journalSourceLabel(entry)}}</span>
											</span>
											<!-- Who added it, then when. Only a person gets an avatar; "Author not recorded" and the like are just text. -->
											<span class="d-flex align-items-center text-secondary nowrap text-small ml-2">
												<ui-user-image *ngIf="entry.author_email" class="mr-1" [email]="entry.author_email" [title]="entry.author_name" size="1.25rem" [shadow]="false"></ui-user-image>
												<span [innerHTML]="entry.author_name | highlight: resultsTerm | safeHtml"></span>
												<ng-container *ngIf="entry.created_datetime"><span class="mx-1">&middot;</span>{{entry.created_datetime | mySQLDateToISO | localizedDate:'mediumDate'}}, {{entry.created_datetime | mySQLDateToISO | localizedDate:'shortTime'}}</ng-container>
											</span>
										</div>
										<div *ngIf="entry.title" class="text-bold mb-1" [innerHTML]="entry.title | highlight: resultsTerm | safeHtml"></div>
										<div *ngIf="entry.snippet" class="text-dark mb-1" [innerHTML]="entry.snippet | highlight: resultsTerm | safeHtml"></div>
										<!-- Highlighted as well: a match can be the author, customer or project rather than the text itself. -->
										<div class="text-secondary text-small text-truncate" [title]="journalMeta(entry)" [innerHTML]="journalMeta(entry) | highlight: resultsTerm | safeHtml"></div>
									</div>
									<div *ngIf="!pagination.count" class="text-center py-5">
										<div class="h3 mb-3">Nothing found in the journal<span *ngIf="resultsTerm"> for &ldquo;{{resultsTerm}}&rdquo;</span><span *ngIf="hasFilters"> with these filters</span></div>
										<button *ngIf="hasFilters" type="button" class="btn btn-outline-secondary" (click)="clearFilters()">Clear filters</button>
									</div>
								</div>

								<!-- Products -->
								<app-product-results *ngIf="activeTab === 'products'"
									[items]="searchResults"
									[pagination]="pagination"
									[query]="resultsTerm"
									[canAdd]="canAdd()"
									[canOpenDetail]="true"
									[canEditProduct]="true"
									[addedProducts]="addedProducts"
									(add)="addProduct($event)"
									(openProduct)="selectProduct($event)"
									(editProduct)="editProduct($event)"></app-product-results>
							</div>

							<app-record-detail *ngIf="showsRecordDetail"
								class="ml-3"
								[kind]="recordKind"
								[title]="recordTitle"
								[subtitle]="recordSubtitle"
								[summary]="recordSummary"
								[bigValue]="recordBigValue"
								[bigLabel]="recordBigLabel"
								[sections]="recordDetail?.sections || []"
								[loading]="recordDetailLoading"
								[currencyId]="currencyId"
								[orgAlias]="orgAlias"
								[link]="recordLink"
								[linkQuery]="recordLinkQuery"
								[linkLabel]="recordLinkLabel"
								[linkAsButton]="activeTab === 'journal'"
								[sectioned]="activeTab !== 'journal'"
								[canAdd]="canAdd()"
								[addedProducts]="addedProducts"
								[groupActionLabel]="recordGroupActionLabel"
								[groupActionSections]="recordGroupActionSections"
								[lineActions]="recordLineActions"
								[lineActionSections]="recordLineActionSections"
								(add)="addProduct($event)"
								(groupAction)="reserveStock($event)"
								(lineAction)="recordLineAction($event)"
								(closed)="closeRecord()">
								<!-- A journal entry: its full text, then the record it is kept on. -->
								<div *ngIf="activeTab === 'journal' && recordDetail as entry" record-body>
									<!-- Who added it and when, with the same avatar the result row shows. Only a person gets one. -->
									<div class="d-flex align-items-center mb-3">
										<ui-user-image *ngIf="entry.author_email" class="mr-2" [email]="entry.author_email" [title]="entry.author_name" size="2rem"></ui-user-image>
										<div>
											<div class="text-dark">{{entry.author_name}}</div>
											<div *ngIf="entry.created_datetime" class="text-secondary text-small">{{entry.created_datetime | mySQLDateToISO | localizedDate:'mediumDate'}}, {{entry.created_datetime | mySQLDateToISO | localizedDate:'shortTime'}}</div>
										</div>
									</div>

									<div *ngIf="entry.text" class="text-dark text-pre mb-3">{{entry.text}}</div>

									<div *ngIf="entry.url || entry.kind === 'report'" class="mb-3">
										<a *ngIf="entry.url" class="btn btn-outline-secondary btn-sm" [href]="entry.url" target="_blank" rel="noopener"><i class="wq mr-1" [ngClass]="journalKindIcon(entry)"></i>{{entry.file_name ? 'Open file' : 'Open document'}}</a>
										<button *ngIf="entry.kind === 'report'" type="button" class="btn btn-outline-secondary btn-sm" (click)="openJournalReport(entry)"><i class="wq wq-clipboard mr-1"></i>Open PDF</button>
									</div>

									<div class="border-top pt-3">
										<div class="d-flex align-items-center mb-1">
											<span class="text-uppercase text-tiny text-secondary mr-2">Original record</span>
											<span class="badge text-tiny text-uppercase nowrap" [ngClass]="journalSourceBadge(entry)"><i *ngIf="journalSourceIcon(entry)" class="wq mr-1" [ngClass]="journalSourceIcon(entry)"></i>{{journalSourceLabel(entry)}}</span>
										</div>
										<div class="text-bold">{{entry.ref}}</div>
										<div class="mb-3">{{entry.record_title}}</div>

										<ng-container *ngIf="entry.customer_id && entry.record_type !== 'customer'">
											<div class="text-uppercase text-tiny text-secondary">Customer</div>
											<div class="mb-3">{{entry.customer_name}}</div>
										</ng-container>

										<ng-container *ngIf="entry.project_id && entry.record_type !== 'project'">
											<div class="text-uppercase text-tiny text-secondary">Project</div>
											<div class="mb-3">{{entry.project_label}}</div>
										</ng-container>

										<div class="text-secondary text-small">This {{journalKindLabel(entry).toLowerCase()}} stays with its original record. Open it to see the rest of the conversation.</div>
									</div>
								</div>
							</app-record-detail>
						</div>
					</ng-container>
					<app-pagination [pagination]="pagination" ui-card-footer></app-pagination>
				</ui-card>
			</ng-template>
		</div>
	</div>
</ui-modal>

<modal-edit-product (close)="productEditModalClosed($event)"></modal-edit-product>
<modal-edit-warehouse-reserve-items (close)="reserveItemsModalClosed($event)"></modal-edit-warehouse-reserve-items>
